import React, { useCallback, useMemo, useState } from 'react';
import { TextInput, TouchableOpacity, View } from 'react-native';
import { Button, Text, XStack, YStack } from 'tamagui';
import { FontAwesome6 } from '@expo/vector-icons';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigation } from '@react-navigation/native';
import { useTranslation } from 'react-i18next';
import {
  CodeField,
  Cursor,
  useBlurOnFulfill,
  useClearByFocusCell,
} from 'react-native-confirmation-code-field';
import apis from '@api/api';
import { DefaultRootState } from '@reduxLocal/persistState';
import {
  HIDE_LOADING_MODAL,
  SHOW_AUTH_MODAL,
  SHOW_LOADING_MODAL,
} from '@reduxLocal/actions/types';
import { responsiveFontSize } from '@utils/responsiveFontSize';
import { responsivePadding } from '@stylesheet';
import {
  handleError,
  handleUserData,
  navigatePostEmailSignupToEasyMode,
} from './utils/authUtils';
import WhatsAppPhoneField from './WhatsAppPhoneField';

type Step = 'phone' | 'otp' | 'password';

type WhatsAppRegisterFlowProps = {
  onCancel: () => void;
};

const CELL_COUNT = 6;

const WhatsAppRegisterFlow = ({ onCancel }: WhatsAppRegisterFlowProps) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigation = useNavigation();
  const theme = useSelector((state: DefaultRootState) => state.app.theme.lightTheme);
  const notificationToken: string = useSelector(
    (state: DefaultRootState) => state.app.notificationToken || '',
  );

  const [step, setStep] = useState<Step>('phone');
  const [phone, setPhone] = useState('');
  const [normalizedPhone, setNormalizedPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [passwordHidden, setPasswordHidden] = useState(true);
  const [busy, setBusy] = useState(false);

  const otpRef = useBlurOnFulfill({ value: otp, cellCount: CELL_COUNT });
  const [otpFieldProps, getCellOnLayoutHandler] = useClearByFocusCell({
    value: otp,
    setValue: setOtp,
  });

  const colors = useMemo(
    () => ({
      inputBackground: theme ? 'rgba(255, 255, 255, 0.95)' : 'rgba(30, 41, 59, 0.95)',
      inputBorder: theme ? 'rgba(148, 163, 184, 0.4)' : 'rgba(148, 163, 184, 0.35)',
      inputText: theme ? '#0f172a' : '#e2e8f0',
      placeholder: theme ? 'rgba(100, 116, 139, 0.7)' : 'rgba(148, 163, 184, 0.75)',
      muted: theme ? '#64748b' : '#94a3b8',
      primary: '#25D366',
      cellBg: theme ? '#f8fafc' : '#1e293b',
    }),
    [theme],
  );

  const showError = useCallback(
    (message: string) => {
      dispatch({
        type: SHOW_AUTH_MODAL,
        data: { content: message, type: 'error' },
      });
    },
    [dispatch],
  );

  const extractError = (error: any, fallback: string) =>
    error?.response?.data?.error
    || error?.response?.data?.message
    || error?.message
    || fallback;

  const handleSendOtp = useCallback(async () => {
    if (!phone.trim()) {
      showError(t('whatsappRegisterPhoneRequired'));
      return;
    }
    setBusy(true);
    dispatch({ type: SHOW_LOADING_MODAL });
    try {
      const res = await apis.sendWhatsAppRegisterOtp({ phone: phone.trim() });
      setNormalizedPhone(res?.phone || phone.trim());
      setStep('otp');
      setOtp('');
    } catch (error) {
      showError(extractError(error, t('whatsappRegisterSendFailed')));
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setBusy(false);
    }
  }, [dispatch, phone, showError, t]);

  const handleVerifyOtp = useCallback(async (codeOverride?: string) => {
    const code = (codeOverride ?? otp).trim();
    if (code.length < CELL_COUNT) {
      showError(t('whatsappRegisterOtpRequired'));
      return;
    }
    setBusy(true);
    dispatch({ type: SHOW_LOADING_MODAL });
    try {
      await apis.verifyWhatsAppRegisterOtp({
        phone: normalizedPhone || phone.trim(),
        code,
      });
      setOtp(code);
      setStep('password');
    } catch (error) {
      setOtp('');
      showError(extractError(error, t('whatsappRegisterOtpInvalid')));
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setBusy(false);
    }
  }, [dispatch, normalizedPhone, otp, phone, showError, t]);

  const handleComplete = useCallback(async () => {
    if (password.length < 8) {
      showError(t('whatsappRegisterPasswordTooShort'));
      return;
    }
    setBusy(true);
    dispatch({ type: SHOW_LOADING_MODAL });
    try {
      const res = await apis.completeWhatsAppRegister({
        phone: normalizedPhone || phone.trim(),
        code: otp,
        password,
        rememberMe: true,
      });
      const amendedData = await handleUserData(res, notificationToken);
      await navigatePostEmailSignupToEasyMode(dispatch, navigation, amendedData, {
        signupMethod: 'whatsapp',
      });
    } catch (error) {
      handleError(dispatch, extractError(error, t('whatsappRegisterCompleteFailed')));
    } finally {
      dispatch({ type: HIDE_LOADING_MODAL });
      setBusy(false);
    }
  }, [dispatch, navigation, normalizedPhone, notificationToken, otp, password, phone, showError, t]);

  const title =
    step === 'phone'
      ? t('whatsappRegisterTitlePhone')
      : step === 'otp'
        ? t('whatsappRegisterTitleOtp')
        : t('whatsappRegisterTitlePassword');

  const subtitle =
    step === 'phone'
      ? t('whatsappRegisterSubtitlePhone')
      : step === 'otp'
        ? t('whatsappRegisterSubtitleOtp', { phone: normalizedPhone || phone })
        : t('whatsappRegisterSubtitlePassword');

  return (
    <YStack
      width="100%"
      maxWidth={400}
      alignSelf="center"
      gap={responsivePadding(16)}
      paddingVertical={responsivePadding(8)}
    >
      <YStack gap={responsivePadding(6)}>
        <Text
          style={{
            fontSize: responsiveFontSize(18),
            fontWeight: '700',
            color: colors.inputText,
            textAlign: 'center',
          }}
        >
          {title}
        </Text>
        <Text
          style={{
            fontSize: responsiveFontSize(13),
            color: colors.muted,
            textAlign: 'center',
          }}
        >
          {subtitle}
        </Text>
      </YStack>

      {step === 'phone' ? (
        <View style={{ width: '100%', alignSelf: 'stretch' }}>
          <WhatsAppPhoneField
            value={phone}
            onChange={setPhone}
            disabled={busy}
            theme={theme}
            placeholder={t('whatsappRegisterPhonePlaceholder')}
            testID="whatsappRegisterPhoneInput"
          />
        </View>
      ) : null}

      {step === 'otp' ? (
        <CodeField
          ref={otpRef}
          {...otpFieldProps}
          value={otp}
          onChangeText={(value) => {
            setOtp(value);
            if (value.length === CELL_COUNT) {
              void handleVerifyOtp(value);
            }
          }}
          cellCount={CELL_COUNT}
          rootStyle={{ justifyContent: 'center', gap: 8 }}
          keyboardType="number-pad"
          textContentType="oneTimeCode"
          renderCell={({ index, symbol, isFocused }) => (
            <Text
              key={index}
              onLayout={getCellOnLayoutHandler(index)}
              style={{
                width: 42,
                height: 48,
                lineHeight: 46,
                fontSize: responsiveFontSize(20),
                borderWidth: 1,
                borderColor: isFocused ? colors.primary : colors.inputBorder,
                borderRadius: 10,
                textAlign: 'center',
                color: colors.inputText,
                backgroundColor: colors.cellBg,
                overflow: 'hidden',
              }}
            >
              {symbol || (isFocused ? <Cursor /> : null)}
            </Text>
          )}
        />
      ) : null}

      {step === 'password' ? (
        <View
          style={{
            backgroundColor: colors.inputBackground,
            borderColor: colors.inputBorder,
            borderWidth: 1,
            borderRadius: 12,
            overflow: 'hidden',
            flexDirection: 'row',
            alignItems: 'center',
          }}
        >
          <TextInput
            value={password}
            onChangeText={setPassword}
            placeholder={t('whatsappRegisterPasswordPlaceholder')}
            placeholderTextColor={colors.placeholder}
            secureTextEntry={passwordHidden}
            autoCapitalize="none"
            autoComplete="new-password"
            textContentType="newPassword"
            style={{
              flex: 1,
              color: colors.inputText,
              fontSize: responsiveFontSize(15),
              paddingVertical: responsivePadding(14),
              paddingHorizontal: responsivePadding(16),
            }}
            editable={!busy}
            testID="whatsappRegisterPasswordInput"
          />
          <TouchableOpacity
            onPress={() => setPasswordHidden((v) => !v)}
            style={{ paddingHorizontal: responsivePadding(14) }}
            accessibilityRole="button"
          >
            <FontAwesome6
              name={passwordHidden ? 'eye' : 'eye-slash'}
              size={18}
              color={passwordHidden ? colors.muted : colors.primary}
            />
          </TouchableOpacity>
        </View>
      ) : null}

      <XStack gap={12} width="100%">
        <Button
          flex={1}
          size="$4"
          backgroundColor={theme ? 'rgba(100, 116, 139, 0.35)' : 'rgba(71, 85, 105, 0.55)'}
          disabled={busy}
          onPress={() => {
            if (step === 'phone') {
              onCancel();
              return;
            }
            if (step === 'otp') {
              setStep('phone');
              setOtp('');
              return;
            }
            setStep('otp');
          }}
        >
          <Text style={{ color: '#fff', fontWeight: '600' }}>{t('Back')}</Text>
        </Button>
        <Button
          flex={1}
          size="$4"
          backgroundColor={colors.primary}
          disabled={busy}
          onPress={() => {
            if (step === 'phone') void handleSendOtp();
            else if (step === 'otp') void handleVerifyOtp();
            else void handleComplete();
          }}
          testID="whatsappRegisterContinueButton"
        >
          <Text style={{ color: '#fff', fontWeight: '600' }}>
            {step === 'password' ? t('Create Account') : t('Continue')}
          </Text>
        </Button>
      </XStack>

      {step === 'otp' ? (
        <TouchableOpacity onPress={() => !busy && void handleSendOtp()} disabled={busy}>
          <Text
            style={{
              textAlign: 'center',
              color: colors.primary,
              fontSize: responsiveFontSize(13),
              fontWeight: '600',
            }}
          >
            {t('whatsappRegisterResendCode')}
          </Text>
        </TouchableOpacity>
      ) : null}
    </YStack>
  );
};

export default WhatsAppRegisterFlow;
